// src/release/constants.ts
var BLUE_GREEN_BLUE = "blue";
var BLUE_GREEN_GREEN = "green";
var DRIFT_LEVEL = {
  INFO: "info",
  WARNING: "warning",
  ERROR: "error"
};
var DRIFT_REASON = {
  BLUE_GREEN_DIVERGED: "blue_green_diverged",
  GREEN_ALIAS_MISMATCH: "green_alias_version_mismatch",
  GREEN_ALIAS_MISSING: "green_alias_missing",
  VERSION_UNRESOLVED: "version_unresolved",
  LAYER_MISMATCH: "layer_mismatch",
  FUNCTION_MISSING: "function_missing",
  GREEN_ALIAS_UNPINNED: "green_alias_unpinned",
  API_GATEWAY_MISSING: "api_gateway_missing",
  NO_FUNCTIONS: "no_functions",
  GREEN_SPLIT: "green_split"
};
var DRIFT_REASON_LABEL = {
  blue_green_diverged: "Blue ahead of green",
  green_alias_version_mismatch: "Green alias mismatch",
  green_alias_missing: "Missing green alias",
  version_unresolved: "Unresolved version",
  layer_mismatch: "Layer mismatch",
  function_missing: "Function deleted",
  green_alias_unpinned: "Green on $LATEST",
  api_gateway_missing: "No API Gateway",
  no_functions: "No functions",
  green_split: "Split live"
};
var VERSION_SYNC_LABEL = {
  in_sync: "Up to date",
  promote_available: "Promote available",
  unpinned: "Live on $LATEST",
  no_green: "No live version",
  no_functions: "No functions",
  unknown: "Unknown"
};
var STALE_AFTER_DAYS = 30;
var LIVE_SCAN_STALE_AFTER_MINUTES = 75;
var UNKNOWN_VERSION_VALUES = [
  "",
  "unknown",
  "ADD VERSION IN ENV VARIABLES"
];
var APP_CATEGORY_ORDER = [
  "external",
  "internal",
  "data",
  "front-end"
];
var APP_CATEGORY_LABEL = {
  external: "External",
  internal: "Internal",
  data: "Data",
  "front-end": "Front-end"
};
var DEFAULT_VISIBLE_CATEGORIES = ["external"];
var AUTHORIZER_LATEST = "$LATEST";
var LAMBDA_VERSION_LATEST = "$LATEST";
function isStageVariableAlias(alias) {
  return typeof alias === "string" && alias.startsWith("${");
}

// src/time/index.ts
var SECOND_MS = 1e3;
var MINUTE_MS = 60 * SECOND_MS;
var HOUR_MS = 60 * MINUTE_MS;
var DAY_MS = 24 * HOUR_MS;

// src/release/status.ts
var UNKNOWN = new Set(UNKNOWN_VERSION_VALUES);
function isKnownVersion(value) {
  return value != null && !UNKNOWN.has(value);
}
function computeSyncState(row) {
  const greenKnown = isKnownVersion(row.green_value);
  const blueKnown = isKnownVersion(row.blue_value);
  if (!greenKnown) return "no_green";
  if (row.green_value === LAMBDA_VERSION_LATEST) return "unpinned";
  if (!blueKnown) return "unknown";
  return row.green_value === row.blue_value ? "in_sync" : "promote_available";
}
function liveSyncState(input) {
  if (input.functions === 0) return "no_functions";
  if (input.live_build === void 0) return computeSyncState(input);
  if (input.live_build === null) {
    const aliased = input.aliased ?? input.functions;
    return aliased != null && aliased > 0 ? "unknown" : computeSyncState(input);
  }
  return computeSyncState({ blue_value: input.blue_value, green_value: input.live_build });
}
function rowSyncState(row) {
  return liveSyncState({
    blue_value: row.blue_value,
    green_value: row.green_value,
    live_build: row.live_builds ? row.live_build ?? null : void 0,
    functions: row.live_builds?.functions,
    aliased: row.live_builds ? row.live_builds.functions - (row.live_builds.no_green_alias ?? 0) : void 0
  });
}
function needsPromotion(row) {
  return computeSyncState(row) === "promote_available";
}
function parseModifiedMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? toMs(value) : null;
  if (UNKNOWN.has(value)) return null;
  if (/^\d+$/.test(value)) return toMs(Number(value));
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}
function toMs(n) {
  return n > 1e12 ? n : n * 1e3;
}
function lastActivityMs(row) {
  const blue = parseModifiedMs(row.blue_last_modified_date);
  const green = parseModifiedMs(row.green_last_modified_date);
  if (blue == null) return green;
  if (green == null) return blue;
  return Math.max(blue, green);
}
function daysSinceMs(ms, nowMs = Date.now()) {
  if (ms == null) return null;
  return Math.floor((nowMs - ms) / DAY_MS);
}
function isRowStale(row, opts = {}) {
  const { thresholdDays = STALE_AFTER_DAYS, nowMs } = opts;
  const days = daysSinceMs(lastActivityMs(row), nowMs);
  return days != null && days >= thresholdDays;
}
var VERSION_TIMESTAMP = /^(\d{4})[-_]{1,2}(\d{2})[-_]{1,2}(\d{2})(?:[T:_ ]{1,2}(\d{2}):(\d{2}):(\d{2}))?/;
function versionTimestampMs(value) {
  if (!value) return null;
  const m = VERSION_TIMESTAMP.exec(value);
  if (!m) return null;
  const part = (i) => Number(m[i] ?? 0);
  const [y, mo, d] = [part(1), part(2), part(3)];
  const ms = Date.UTC(y, mo - 1, d, part(4), part(5), part(6));
  const check = new Date(ms);
  if (check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null;
  return ms;
}
function versionDirection(target, live) {
  if (target != null && target === live) return "same";
  const t = versionTimestampMs(target);
  const l = versionTimestampMs(live);
  if (t == null || l == null) return "unknown";
  if (t === l) return "same";
  return t > l ? "newer" : "older";
}
function liveSplit(builds) {
  const entries = Object.entries(builds?.builds ?? {});
  if (entries.length < 2) return null;
  return entries.map(([build, functions]) => ({ build, functions })).sort((a, b) => b.functions - a.functions || a.build.localeCompare(b.build));
}
function describeBuilds(counts) {
  return counts.map((c) => `${c.build} on ${c.functions}`).join(", ");
}
function greenSplitOf(detail) {
  const finding = (detail?.drift ?? []).find((f) => f.reason === "green_split");
  if (!finding?.details) return null;
  return finding;
}
function liveScanAgeMinutes(rows, nowMs = Date.now()) {
  let newest = null;
  for (const row of rows) {
    const builds = row.live_builds;
    if (!builds || !("changed_at" in builds)) continue;
    const scanned = builds.scanned_at;
    if (!scanned) continue;
    const ms = Date.parse(scanned);
    if (!Number.isNaN(ms) && (newest == null || ms > newest)) newest = ms;
  }
  return newest == null ? null : Math.max(0, Math.round((nowMs - newest) / 6e4));
}
function isLiveScanStale(ageMinutes) {
  return ageMinutes != null && ageMinutes > LIVE_SCAN_STALE_AFTER_MINUTES;
}

// src/release/categories.ts
var APP_CATEGORY_OVERRIDES = {};
var CATEGORY_SUFFIXES = [
  ["-front-end", "front-end"],
  ["-internal", "internal"],
  ["-data", "data"]
];
function classifyApplication(applicationName) {
  const override = APP_CATEGORY_OVERRIDES[applicationName];
  if (override) return override;
  for (const [suffix, category] of CATEGORY_SUFFIXES) {
    if (applicationName.endsWith(suffix)) return category;
  }
  return "external";
}
var DECOMMISSIONED_APPLICATIONS = /* @__PURE__ */ new Set([
  "blocknative-mgmt-api",
  "entities-mgmt-api-internal",
  "labels-mgmt-api-internal",
  "share-transactions-graph-api",
  // DECOMMISSIONED.md in the repo
  "share-transactions-graph-data",
  // DECOMMISSIONED.md in the repo
  "tracer-mgmt"
]);
function isDecommissioned(applicationName) {
  return DECOMMISSIONED_APPLICATIONS.has(applicationName);
}

// src/release/conditions.ts
var VERSION_CONDITION_ORDER = [
  "promote_available",
  "unpinned",
  "split",
  "no_green",
  "unknown",
  "stale",
  "in_sync",
  "no_functions"
];
var VERSION_CONDITION_LABEL = {
  ...VERSION_SYNC_LABEL,
  split: "Split live",
  stale: "Stale"
};
var VERSION_CONDITION_TITLE = {
  in_sync: "Live runs the latest deployed build.",
  promote_available: "A newer build is deployed (blue) but not live.",
  unpinned: "Live points at $LATEST: every deploy goes live without a promotion.",
  no_green: "Nothing is live: no promotion recorded and no green alias read.",
  no_functions: "No Lambda functions in this region (Terraform-only or retired repo).",
  unknown: "Live and latest cannot be compared.",
  split: "The functions' green aliases run more than one build.",
  stale: "No deploy or promotion for a while."
};
var ATTENTION_CONDITIONS = /* @__PURE__ */ new Set([
  "promote_available",
  "unpinned",
  "split",
  "no_green",
  "unknown",
  "stale"
]);
function rowConditions(row, opts = {}) {
  const state = rowSyncState(row);
  const conditions = /* @__PURE__ */ new Set([state]);
  if (liveSplit(row.live_builds)) conditions.add("split");
  if (state !== "no_functions" && isRowStale(row, opts)) conditions.add("stale");
  return conditions;
}
function needsAttention(conditions) {
  for (const condition of conditions) if (ATTENTION_CONDITIONS.has(condition)) return true;
  return false;
}
function matchesConditions(conditions, selected) {
  if (selected.size === 0) return true;
  for (const condition of conditions) if (selected.has(condition)) return true;
  return false;
}

export { APP_CATEGORY_LABEL, APP_CATEGORY_ORDER, APP_CATEGORY_OVERRIDES, ATTENTION_CONDITIONS, AUTHORIZER_LATEST, BLUE_GREEN_BLUE, BLUE_GREEN_GREEN, DECOMMISSIONED_APPLICATIONS, DEFAULT_VISIBLE_CATEGORIES, DRIFT_LEVEL, DRIFT_REASON, DRIFT_REASON_LABEL, LAMBDA_VERSION_LATEST, LIVE_SCAN_STALE_AFTER_MINUTES, STALE_AFTER_DAYS, UNKNOWN_VERSION_VALUES, VERSION_CONDITION_LABEL, VERSION_CONDITION_ORDER, VERSION_CONDITION_TITLE, VERSION_SYNC_LABEL, classifyApplication, computeSyncState, daysSinceMs, describeBuilds, greenSplitOf, isDecommissioned, isKnownVersion, isLiveScanStale, isRowStale, isStageVariableAlias, lastActivityMs, liveScanAgeMinutes, liveSplit, liveSyncState, matchesConditions, needsAttention, needsPromotion, parseModifiedMs, rowConditions, rowSyncState, versionDirection, versionTimestampMs };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map